import { useCallback, useEffect, useState } from "react";
import "./paneles.css";

/**
 * El panel de contabilidad: los huecos, no los listados.
 *
 * ── Qué enseña y por qué tan poco ───────────────────────────────────────
 *
 * Moneyfix ya tiene diez pantallas y las hace bien: lee facturas con IA, lleva
 * el perfil fiscal de verdad —modelos 303, 130 y 390, prorrata, recargo de
 * equivalencia—, cierra trimestres y exporta a Excel. Volver a pintar aquí sus
 * gastos sería trabajo tirado y dos sitios donde mirar lo mismo.
 *
 * Lo que Moneyfix NO puede saber es lo que sabe BilboBot: qué tiene contratado
 * cada cliente, cuándo renueva cada dominio, qué se facturó en Holded y cuánto
 * cuesta la IA. Así que esta pantalla enseña sólo eso: los DESCUADRES entre las
 * dos mitades. Si no hay ninguno, lo dice y se calla.
 *
 * ── Y por qué no hay ningún botón ───────────────────────────────────────
 *
 * Porque BilboBot no escribe en Moneyfix. Su `PUT /api/state` reemplaza el
 * estado entero sin comprobar si alguien ha escrito en medio, y con el cuerpo
 * vacío borra los ámbitos con todo dentro. Eso se dice en la propia pantalla,
 * abajo, para que la ausencia de botones se lea como una decisión y no como
 * algo a medio hacer.
 */

interface Descuadre {
  clase: string;
  titulo: string;
  importe?: number;
  detalle: string;
  ojo: string;
}

interface Datos {
  configurado: boolean;
  porQue?: string;
  mes?: string;
  ambitos?: {
    id: string;
    nombre: string;
    gastos: { real: number; cuotas: number; cuantos: number };
    ingresos: { conIva: number; base: number; cuantos: number };
  }[];
  descuadres?: Descuadre[];
  cuantos?: { gastos: number; ingresos: number; ambitos: number };
  soloLectura: boolean;
  porQueSoloLectura: string;
}

const eur = (n: number) =>
  n.toLocaleString("es-ES", { style: "currency", currency: "EUR" });

/** Un icono por clase de descuadre, para distinguirlos sin leer. */
const ICONO: Record<string, string> = {
  "facturado-sin-ingresar": "📄",
  "renovacion-sin-gasto": "🔄",
  "ia-sin-gasto": "🤖",
};

export function ContabilidadPanel() {
  const [datos, setDatos] = useState<Datos | null>(null);
  const [error, setError] = useState<string | null>(null);

  const cargar = useCallback(async () => {
    try {
      const r = await fetch("/api/moneyfix", { credentials: "same-origin" });
      if (!r.ok) throw new Error(`El servidor contestó ${r.status}`);
      setDatos((await r.json()) as Datos);
      setError(null);
    } catch (e) {
      setError(String(e));
    }
  }, []);

  /*
   * Cada minuto, no cada cinco segundos como los otros paneles. La contabilidad
   * no cambia sola: los descuadres son de un mes entero y refrescarlos a ese
   * ritmo sería pedirle casi un mega a Moneyfix doce veces por minuto.
   */
  useEffect(() => {
    void cargar();
    const t = setInterval(() => void cargar(), 60_000);
    return () => clearInterval(t);
  }, [cargar]);

  if (error && !datos)
    return (
      <main className="leadslink pnl-leadslink">
        <p className="vacio">No se pudo leer la contabilidad: {error}</p>
      </main>
    );

  if (!datos)
    return (
      <main className="leadslink pnl-leadslink">
        <p className="vacio">Mirando…</p>
      </main>
    );

  if (!datos.configurado || datos.porQue)
    return (
      <main className="leadslink pnl-leadslink">
        <section className="bloque apagado">
          <h2>Contabilidad</h2>
          <p className="pie">{datos.porQue}</p>
          <p className="aviso">{datos.porQueSoloLectura}</p>
        </section>
      </main>
    );

  const descuadres = datos.descuadres ?? [];

  return (
    <main className="leadslink pnl-leadslink">
      <header className="pnl-cabecera ll-cabecera">
        <div>
          <h1>Contabilidad</h1>
          <p className="pnl-sub">Lo que dice Moneyfix, contrastado con la cartera. Aquí se mira; no se apunta nada.</p>
        </div>
      </header>
      <SeccionDescuadres descuadres={descuadres} mes={datos.mes} />
      <SeccionAmbitos ambitos={datos.ambitos ?? []} mes={datos.mes} />

      <section className="bloque apagado">
        <h2>Por qué aquí no se apunta nada</h2>
        <p className="pie">{datos.porQueSoloLectura}</p>
        {datos.cuantos && (
          <p className="pie">
            Moneyfix tiene {datos.cuantos.gastos} gastos y{" "}
            {datos.cuantos.ingresos} ingresos en {datos.cuantos.ambitos} ámbitos.
          </p>
        )}
      </section>
    </main>
  );
}

/**
 * Los huecos, arriba del todo.
 *
 * Es lo único de esta pantalla que pide algo de Jorge, así que va primero. Cada
 * uno lleva su «ojo»: todos estos cruces comparan cosas que se apuntan de
 * formas y con retrasos distintos, y un descuadre es una pregunta que hacerse,
 * no un error demostrado. Decirlo debajo de cada uno evita que la pantalla
 * presente lo segundo.
 */
function SeccionDescuadres({
  descuadres,
  mes,
}: {
  descuadres: Descuadre[];
  mes?: string;
}) {
  if (descuadres.length === 0)
    return (
      <section className="bloque">
        <h2>Cuadra</h2>
        <p className="pie">
          No hay nada descuadrado entre Moneyfix y lo que sabe BilboBot
          {mes ? ` en ${mes}` : ""}: lo facturado está apuntado, las
          renovaciones que han pasado figuran como gasto, y la IA del mes
          también.
        </p>
      </section>
    );

  return (
    <section className="bloque urgente">
      <h2>Huecos entre Moneyfix y la cartera ({descuadres.length})</h2>
      <ul className="descuadres">
        {descuadres.map((d, i) => (
          <li key={`${d.clase}-${i}`}>
            <div className="cabeza">
              <span className="icono" aria-hidden="true">
                {ICONO[d.clase] ?? "•"}
              </span>
              <span className="que">{d.titulo}</span>
              {d.importe !== undefined && (
                <span className="importe">{eur(d.importe)}</span>
              )}
            </div>
            <div className="dijo">{d.detalle}</div>
            <div className="ojo">{d.ojo}</div>
          </li>
        ))}
      </ul>
      <p className="pie">
        Se apuntan en Moneyfix, no aquí. BilboBot los encuentra; corregirlos es
        tuyo.
      </p>
    </section>
  );
}

/** El mes por ámbito. Contexto, no trabajo: por eso va debajo y en pequeño. */
function SeccionAmbitos({
  ambitos,
  mes,
}: {
  ambitos: NonNullable<Datos["ambitos"]>;
  mes?: string;
}) {
  const conAlgo = ambitos.filter(
    (a) => a.gastos.cuantos > 0 || a.ingresos.cuantos > 0 || a.gastos.cuotas > 0,
  );
  if (conAlgo.length === 0) return null;

  return (
    <section className="bloque">
      <h2>El mes por ámbito{mes ? ` · ${mes}` : ""}</h2>
      <div className="tabla-scroll">
        <table className="ambitos">
          <thead>
            <tr>
              <th>Ámbito</th>
              <th>Gastado</th>
              <th>Cuotas</th>
              <th>Ingresado</th>
            </tr>
          </thead>
          <tbody>
            {conAlgo.map((a) => (
              <tr key={a.id}>
                <td>{a.nombre}</td>
                <td className="n">{eur(a.gastos.real)}</td>
                {/*
                  Las cuotas van en su propia columna a propósito: un
                  «recurring + budgetOnly» es lo que se presupuesta al mes, no
                  un cargo de una fecha. Sumarlas con lo gastado contaría dos
                  veces la misma hipoteca.
                */}
                <td className="n tenue">{eur(a.gastos.cuotas)}</td>
                <td className="n">{eur(a.ingresos.base)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="pie">
        «Gastado» son cargos con fecha de este mes. «Cuotas» es lo periódico que
        hay presupuestado, que no es un cargo. Los ingresos, sin IVA.
      </p>
    </section>
  );
}
